/**
 * Remote Work Report Service — rozliczenie pracy zdalnej.
 *
 * localStorage key: "hr_remote_reports" — obiekt keyed `${userId}_${date}`.
 *
 * Odpowiada za:
 * - CRUD raportów dziennych pracy zdalnej
 * - Obliczanie okna czasowego formularza (1 dzień przed – 1 dzień po zdalnej)
 * - Walidację tabeli zadań (sekwencyjność, brak nakładania, limit godzin, min 20 znaków opisu)
 * - Integrację z hrDataService (tworzenie wniosku HR przy złożeniu)
 * - Powiadomienia do HR i przełożonego
 * - Mechanizm ponownego otwarcia raportu przez HR
 */

import { HR_REQUEST_STATUS } from "../constants/hrRequestTypes";
import hrDataService from "./hrDataService";
import { apiService } from "../api/apiService";
import { readJSON, writeJSON, generateId, now } from "../utils/localStorageUtils";

// ─── Helpers ──────────────────────────────────────────────────────
const STORAGE_KEY = "hr_remote_reports";

// [FIX 2026-08-01] Statusy wniosku HR, które NIE blokują ponownego złożenia raportu za ten sam
// dzień (odrzucony/anulowany raport można złożyć od nowa). Spójne z dedupem SERWEROWYM
// w HrController.CreateRequest — front tylko dokłada czytelną ścieżkę, backend i tak odrzuci.
const INACTIVE_HR_STATUSES = [
    "Rejected", "RejectedByManager", "RejectedByDirector", "RejectedByCfo", "RejectedByPresident", "Cancelled",
];
const isActiveHrStatus = (status) => !INACTIVE_HR_STATUSES.includes(String(status || ""));

// Status wniosku HR (backend) → status lokalnego raportu (localStorage).
// null = nie nadpisuj lokalnego stanu (np. Cancelled — pracownik może zacząć od nowa).
const mapHrStatusToReportStatus = (status) => {
    const s = String(status || "");
    if (!s) return null;
    if (s === "Completed") return "approved";
    if (s === "CorrectionRequested") return "reopened";
    if (s.startsWith("Rejected")) return "rejected";
    if (s === "Cancelled") return null;
    return "submitted"; // Pending* / ApprovedBy* / Resubmitted — wniosek w obiegu
};

// ─── Predefiniowane typy zadań ────────────────────────────────────
export const REMOTE_TASK_TYPES = [
    { value: "analiza", label: "Analiza i planowanie" },
    { value: "programowanie", label: "Programowanie / implementacja" },
    { value: "dokumentacja", label: "Dokumentacja" },
    { value: "spotkanie", label: "Spotkanie / wideokonferencja" },
    { value: "testowanie", label: "Testowanie" },
    { value: "komunikacja", label: "Komunikacja (e-mail, chat)" },
    { value: "szkolenie", label: "Szkolenie / e-learning" },
    { value: "administracja", label: "Administracja" },
    { value: "inne", label: "Inne" },
];

// ─── Default schedule (fallback) ──────────────────────────────────
const DEFAULT_SCHEDULE = [
    { day: 0, from: "08:00", to: "16:00", isWorkDay: true, location: "biuro" },
    { day: 1, from: "08:00", to: "16:00", isWorkDay: true, location: "biuro" },
    { day: 2, from: "08:00", to: "16:00", isWorkDay: true, location: "biuro" },
    { day: 3, from: "08:00", to: "16:00", isWorkDay: true, location: "biuro" },
    { day: 4, from: "08:00", to: "16:00", isWorkDay: true, location: "biuro" },
    { day: 5, from: "", to: "", isWorkDay: false, location: "" },
    { day: 6, from: "", to: "", isWorkDay: false, location: "" },
];

// ─── Time utilities ───────────────────────────────────────────────

/**
 * Oblicza minuty między dwoma stringami "HH:MM".
 */
function calculateDuration(from, to) {
    if (!from || !to) return 0;
    const [fh, fm] = from.split(":").map(Number);
    const [th, tm] = to.split(":").map(Number);
    const fromMin = fh * 60 + fm;
    const toMin = th * 60 + tm;
    return Math.max(0, toMin - fromMin);
}

/**
 * Dodaje/odejmuje godziny od stringa "HH:MM" i zwraca "HH:MM".
 */
function shiftTime(timeStr, hoursOffset) {
    if (!timeStr) return "08:00";
    const [h, m] = timeStr.split(":").map(Number);
    let totalMin = h * 60 + m + hoursOffset * 60;
    if (totalMin < 0) totalMin = 0;
    if (totalMin > 23 * 60 + 59) totalMin = 23 * 60 + 59;
    const nh = Math.floor(totalMin / 60);
    const nm = totalMin % 60;
    return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`;
}

/**
 * Zwraca dzień tygodnia JS Date jako index Mon=0..Sun=6 (jak grafik).
 */
function getGrafikDayIndex(date) {
    const jsDay = date.getDay(); // 0=Sun, 1=Mon...
    return jsDay === 0 ? 6 : jsDay - 1;
}

/**
 * Dla danej daty ISO i grafiku zwraca schedule entry dla tego dnia.
 */
function getScheduleForDate(schedule, dateISO) {
    const d = new Date(dateISO + "T12:00:00");
    const idx = getGrafikDayIndex(d);
    const sched = schedule || DEFAULT_SCHEDULE;
    return sched[idx] || DEFAULT_SCHEDULE[idx] || DEFAULT_SCHEDULE[0];
}

/**
 * Oblicza okno dostępności formularza.
 * Nowa logika: 1 dzień PRZED datą zdalnej, 2 dni PO dacie zdalnej (cały dzień).
 * Zwraca { opens, closes, opensISO, closesISO, scheduledFrom, scheduledTo }
 */
function getFormWindow(schedule, dateISO) {
    const day = getScheduleForDate(schedule, dateISO);
    const from = day.from || "08:00";
    const to = day.to || "16:00";

    // [RAPORT ZDALNY 2026-07-30] Okno wg spec właściciela: DZIEŃ PRZED (00:00) do DNIA PO (23:59).
    // Backend waliduje to samo serwerowo (HrController) + godziny pracy — front to tylko UX.
    const remoteDate = new Date(dateISO + "T00:00:00");
    const windowOpens = new Date(remoteDate);
    windowOpens.setDate(windowOpens.getDate() - 1);
    windowOpens.setHours(0, 0, 0, 0);

    const windowCloses = new Date(remoteDate);
    windowCloses.setDate(windowCloses.getDate() + 1);
    windowCloses.setHours(23, 59, 59, 999);

    return {
        opens: "00:00",
        closes: "23:59",
        opensISO: windowOpens.toISOString(),
        closesISO: windowCloses.toISOString(),
        scheduledFrom: from,
        scheduledTo: to,
    };
}

/**
 * Sprawdza czy teraz jest w oknie formularza.
 */
function isFormWindowOpen(schedule, dateISO) {
    const { opensISO, closesISO } = getFormWindow(schedule, dateISO);
    const nowMs = Date.now();
    const opensMs = new Date(opensISO).getTime();
    const closesMs = new Date(closesISO).getTime();
    return nowMs >= opensMs && nowMs <= closesMs;
}

// ═══════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════

const remoteWorkReportService = {
    // ── CRUD ──────────────────────────────────────────────────────

    /**
     * Pobiera raport na konkretny dzień.
     */
    getReport(userId, dateISO) {
        const all = readJSON(STORAGE_KEY, {});
        const key = `${userId}_${dateISO}`;
        return all[key] || null;
    },

    /**
     * Pobiera raport po ID.
     */
    getReportById(reportId) {
        const all = readJSON(STORAGE_KEY, {});
        for (const report of Object.values(all)) {
            if (report && report.id === reportId) return report;
        }
        return null;
    },

    /**
     * Tworzy nowy raport lub zwraca istniejący.
     */
    createOrGetReport(userId, userName, sourceRequest, schedule) {
        const dateISO = sourceRequest.formData?.date;
        if (!dateISO) throw new Error("Brak daty w źródłowym wniosku");

        const existing = this.getReport(userId, dateISO);
        if (existing) return existing;

        // Pobierz employee details
        let contractHours = 8;
        try {
            const raw = localStorage.getItem(`hr_emp_details_${userId}`);
            if (raw) {
                const det = JSON.parse(raw);
                contractHours = det.workHours || 8;
            }
        } catch (error) { console.error("Błąd kalkulacji raportu pracy zdalnej:", error); }

        const window = getFormWindow(schedule, dateISO);

        const report = {
            id: generateId("rwr"),
            userId,
            userName: userName || userId,
            sourceRequestId: sourceRequest.id,
            sourceRequestNumber: sourceRequest.requestNumber || "",
            date: dateISO,
            scheduledFrom: window.scheduledFrom,
            scheduledTo: window.scheduledTo,
            contractHours,
            windowOpens: window.opensISO,
            windowCloses: window.closesISO,
            geolocation: null,
            loginTime: null,
            logoutTime: null,
            tasks: [],
            totalMinutes: 0,
            maxMinutes: contractHours * 60,
            status: "draft",
            midDayReminderShown: false,
            history: [
                { action: "REPORT_CREATED", timestamp: now(), details: `Raport utworzony dla daty ${dateISO}` },
            ],
            createdAt: now(),
            submittedAt: null,
            reopenedAt: null,
            reopenedBy: null,
            submittedRequestId: null,
        };

        this.saveReport(report);
        return report;
    },

    /**
     * Zapisuje raport do localStorage.
     */
    saveReport(report) {
        const all = readJSON(STORAGE_KEY, {});
        const key = `${report.userId}_${report.date}`;
        all[key] = report;
        writeJSON(STORAGE_KEY, all);
    },

    /**
     * [RAPORT ZDALNY 2026-07-30] Odpowiedzi na pola wskazane przez HR per pracownik
     * (konfiguracja w /admin/remote-reports). Obiekt {etykieta: wartość} —
     * trafia do wniosku (formData.customFields), a z nim do akceptacji i e-teczki.
     */
    setCustomFields(reportId, customFields) {
        const report = this.getReportById(reportId);
        if (!report) return null;
        report.customFields = customFields || null;
        this.saveReport(report);
        return report;
    },

    // ── Task Management ──────────────────────────────────────────

    /**
     * Dodaje zadanie do raportu.
     */
    addTask(reportId, taskData) {
        const report = this.getReportById(reportId);
        if (!report) throw new Error("Raport nie znaleziony");
        if (report.status === "submitted") throw new Error("Raport został już złożony");

        const task = {
            id: generateId("task"),
            taskType: taskData.taskType || "",
            description: taskData.description || "",
            timeFrom: taskData.timeFrom || "",
            timeTo: taskData.timeTo || "",
            durationMinutes: calculateDuration(taskData.timeFrom, taskData.timeTo),
            createdAt: now(),
            lastEditedAt: now(),
            editCount: 0,
        };

        report.tasks.push(task);
        report.totalMinutes = report.tasks.reduce((s, t) => s + t.durationMinutes, 0);
        report.history.push({
            action: "TASK_ADDED",
            timestamp: now(),
            details: `Dodano zadanie: ${REMOTE_TASK_TYPES.find(t => t.value === task.taskType)?.label || task.taskType} (${task.timeFrom}–${task.timeTo})`,
        });

        this.saveReport(report);
        return { report, task };
    },

    /**
     * Edytuje zadanie w raporcie.
     */
    updateTask(reportId, taskId, data) {
        const report = this.getReportById(reportId);
        if (!report) throw new Error("Raport nie znaleziony");
        if (report.status === "submitted") throw new Error("Raport został już złożony");

        const idx = report.tasks.findIndex(t => t.id === taskId);
        if (idx === -1) throw new Error("Zadanie nie znalezione");

        const task = report.tasks[idx];
        const updated = { ...task, ...data };
        updated.durationMinutes = calculateDuration(updated.timeFrom, updated.timeTo);
        updated.lastEditedAt = now();
        updated.editCount = (task.editCount || 0) + 1;

        report.tasks[idx] = updated;
        report.totalMinutes = report.tasks.reduce((s, t) => s + t.durationMinutes, 0);
        report.history.push({
            action: "TASK_EDITED",
            timestamp: now(),
            details: `Edytowano zadanie #${idx + 1}: ${REMOTE_TASK_TYPES.find(t => t.value === updated.taskType)?.label || updated.taskType} (edycja nr ${updated.editCount})`,
        });

        this.saveReport(report);
        return report;
    },

    /**
     * Usuwa zadanie z raportu.
     */
    removeTask(reportId, taskId) {
        const report = this.getReportById(reportId);
        if (!report) throw new Error("Raport nie znaleziony");
        if (report.status === "submitted") throw new Error("Raport został już złożony");

        const idx = report.tasks.findIndex(t => t.id === taskId);
        if (idx === -1) throw new Error("Zadanie nie znalezione");

        const removed = report.tasks[idx];
        report.tasks.splice(idx, 1);
        report.totalMinutes = report.tasks.reduce((s, t) => s + t.durationMinutes, 0);
        report.history.push({
            action: "TASK_DELETED",
            timestamp: now(),
            details: `Usunięto zadanie: ${REMOTE_TASK_TYPES.find(t => t.value === removed.taskType)?.label || removed.taskType} (${removed.timeFrom}–${removed.timeTo})`,
        });

        this.saveReport(report);
        return report;
    },

    // ── Geolocation ──────────────────────────────────────────────

    /**
     * Zapisuje geolokalizację i login time.
     */
    saveGeolocation(reportId, geoData) {
        const report = this.getReportById(reportId);
        if (!report) return;

        report.geolocation = {
            latitude: geoData.latitude,
            longitude: geoData.longitude,
            accuracy: geoData.accuracy,
            capturedAt: geoData.capturedAt || now(),
        };

        if (!report.loginTime) {
            report.loginTime = now();
            report.history.push({
                action: "LOGIN_RECORDED",
                timestamp: now(),
                details: `Logowanie zarejestrowane. Lokalizacja: ${geoData.latitude.toFixed(4)}, ${geoData.longitude.toFixed(4)} (dokładność: ${Math.round(geoData.accuracy)}m)`,
            });
        }

        this.saveReport(report);
        return report;
    },

    // ── Submit ────────────────────────────────────────────────────

    /**
     * Waliduje zadania.
     */
    validateTasks(tasks, maxMinutes) {
        const errors = [];
        if (tasks.length === 0) {
            errors.push("Dodaj przynajmniej jedno zadanie.");
            return errors;
        }

        let totalMin = 0;
        for (let i = 0; i < tasks.length; i++) {
            const t = tasks[i];
            if (!t.taskType) errors.push(`Zadanie #${i + 1}: wybierz typ zadania.`);
            if (!t.description || !t.description.trim()) {
                errors.push(`Zadanie #${i + 1}: wpisz opis.`);
            } else if (t.description.trim().length < 20) {
                errors.push(`Zadanie #${i + 1}: opis musi mieć minimum 20 znaków (aktualnie: ${t.description.trim().length}).`);
            }
            if (!t.timeFrom || !t.timeTo) {
                errors.push(`Zadanie #${i + 1}: uzupełnij przedział czasowy.`);
                continue;
            }
            if (t.durationMinutes <= 0) {
                errors.push(`Zadanie #${i + 1}: czas "Do" musi być późniejszy niż "Od".`);
            }

            // Sprawdź nakładanie z poprzednimi
            for (let j = 0; j < i; j++) {
                const prev = tasks[j];
                if (prev.timeFrom && prev.timeTo && t.timeFrom && t.timeTo) {
                    const pStart = prev.timeFrom.replace(":", "");
                    const pEnd = prev.timeTo.replace(":", "");
                    const tStart = t.timeFrom.replace(":", "");
                    const tEnd = t.timeTo.replace(":", "");
                    if (tStart < pEnd && tEnd > pStart) {
                        errors.push(`Zadanie #${i + 1} nakłada się czasowo z zadaniem #${j + 1}.`);
                    }
                }
            }

            totalMin += t.durationMinutes;
        }

        if (totalMin > maxMinutes) {
            const h = Math.floor(maxMinutes / 60);
            const m = maxMinutes % 60;
            errors.push(`Łączny czas pracy (${Math.floor(totalMin / 60)}h ${totalMin % 60}min) przekracza limit ${h}h${m > 0 ? ` ${m}min` : ""}.`);
        }

        return errors;
    },

    /**
     * Składa raport — tworzy wniosek HR.
     */
    async submitReport(reportId, user) {
        const report = this.getReportById(reportId);
        if (!report) throw new Error("Raport nie znaleziony");
        if (report.status === "submitted") throw new Error("Raport został już złożony");

        const errors = this.validateTasks(report.tasks, report.maxMinutes);
        if (errors.length > 0) {
            throw new Error(errors.join("\n"));
        }

        // [FIX 2026-08-01] WERYFIKACJA SERWEROWA przed wysyłką — na prodzie powstał DUPLIKAT
        // wniosku za ten sam dzień (podwójny POST; lokalny guard zapisywał się dopiero PO
        // sukcesie). Backend = źródło prawdy: jeśli aktywny wniosek za tę datę już istnieje, to
        // (a) CorrectionRequested → poprawka idzie Resubmitted po ISTNIEJĄCYM wniosku,
        // (b) inny aktywny status → oznacz lokalny raport jako złożony i zablokuj duplikat.
        let resubmitTarget = null;
        const existingDaily = this._buildDailyRequestsByDate(await this._getMyDailyReportRequests(user.id));
        const existing = existingDaily.get(report.date);
        if (existing && isActiveHrStatus(existing.status)) {
            if (existing.status === "CorrectionRequested") {
                resubmitTarget = existing;
            } else {
                if (!Array.isArray(report.history)) report.history = [];
                report.status = "submitted";
                report.submittedAt = report.submittedAt || now();
                report.submittedRequestId = existing.id;
                report.history.push({
                    action: "SYNCED_FROM_BACKEND",
                    timestamp: now(),
                    details: `Wniosek ${existing.requestNumber || existing.id} za ${report.date} już istnieje na serwerze — raport oznaczony jako złożony (duplikat zablokowany).`,
                });
                this.saveReport(report);
                throw new Error("Rozliczenie za ten dzień zostało już złożone.");
            }
        }

        // Przygotuj podsumowanie zadań
        const tasksSummary = report.tasks.map((t, i) => {
            const typeLabel = REMOTE_TASK_TYPES.find(rt => rt.value === t.taskType)?.label || t.taskType;
            return `${i + 1}. [${t.timeFrom}–${t.timeTo}] ${typeLabel}: ${t.description}`;
        }).join("\n");

        const totalH = Math.floor(report.totalMinutes / 60);
        const totalM = report.totalMinutes % 60;

        // Status sprzed wysyłki (draft ALBO reopened — cykl korekty) — do przywrócenia przy błędzie,
        // żeby zwrócony do korekty raport nie „zamykał się" po nieudanej próbie ponownej wysyłki.
        const prevStatus = report.status;
        report.status = "submitted";
        report.submittedAt = now();
        report.logoutTime = now();
        report.history.push({
            action: "SUBMITTED",
            timestamp: now(),
            details: `Rozliczenie złożone. Łącznie: ${totalH}h ${totalM}min. Zadania: ${report.tasks.length}.`,
        });

        // Utwórz wniosek HR
        try {
            const fakeUser = {
                id: user.id,
                firstName: user.firstName || user.name?.split(" ")[0] || "",
                lastName: user.lastName || user.name?.split(" ").slice(1).join(" ") || "",
            };

            const loginTimeFormatted = report.loginTime
                ? new Date(report.loginTime).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })
                : "—";
            const logoutTimeFormatted = report.logoutTime
                ? new Date(report.logoutTime).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })
                : "—";

            const formData = {
                date: report.date,
                loginTime: loginTimeFormatted,
                logoutTime: logoutTimeFormatted,
                totalHours: `${totalH}h ${totalM}min`,
                tasksSummary,
                reportId: report.id,
                // Strukturyzowane dane zadań
                tasks: report.tasks.map(t => ({
                    taskType: t.taskType,
                    description: t.description,
                    timeFrom: t.timeFrom,
                    timeTo: t.timeTo,
                    durationMinutes: t.durationMinutes,
                    createdAt: t.createdAt,
                    lastEditedAt: t.lastEditedAt,
                    editCount: t.editCount || 0,
                })),
                employeeName: `${fakeUser.firstName} ${fakeUser.lastName}`.trim(),
                employeeDepartment: user.departmentName || "",
                employeePosition: user.jobPosition || "",
                submittedAt: now(),
                geolocation: report.geolocation || null,
                // [RAPORT ZDALNY 2026-07-30] Odpowiedzi na pola WSKAZANE PRZEZ HR per pracownik
                // (konfiguracja /admin/remote-reports) — trafiają do wniosku i e-teczki.
                customFields: report.customFields || null,
            };

            if (resubmitTarget) {
                // [FIX 2026-08-01] CYKL KOREKTY: NIE twórz nowego wniosku — reaktywuj ISTNIEJĄCY
                // (Resubmitted przywraca kroki akceptacji w HrService) z poprawionym FormDataJson.
                await hrDataService.resubmitRequest(resubmitTarget.id, user, formData);
                report.submittedRequestId = resubmitTarget.id;
                report.history.push({
                    action: "RESUBMITTED",
                    timestamp: now(),
                    details: `Poprawiony raport wysłany ponownie do akceptacji (wniosek ${resubmitTarget.requestNumber || resubmitTarget.id}).`,
                });
            } else {
                const created = await hrDataService.createRequest(fakeUser, "REMOTE_WORK_DAILY_REPORT", formData);
                report.submittedRequestId = created.id;
            }
            this.saveReport(report); // zapis (status=submitted) TYLKO po powstaniu/reaktywacji wniosku HR
        } catch (e) {
            // Koniec fałszywego „wysłano": jeśli wniosek HR nie powstał, NIE oznaczamy jako złożony i rzucamy.
            report.status = prevStatus;
            console.error("Błąd wysyłki rozliczenia pracy zdalnej:", e);
            // Pokaż REALNY komunikat backendu (400 z {error} — np. okno/godziny pracy), nie sztywny tekst.
            const serverMsg = e?.response?.data?.error
                || e?.response?.data?.message
                || (e?.response?.status ? `Błąd serwera (${e.response.status})` : e.message);
            throw new Error(`Nie udało się wysłać rozliczenia: ${serverMsg}`);
        }

        // Powiadomienia
        if (hrDataService._dispatchNotification) {
            hrDataService._dispatchNotification("REMOTE_REPORT_SUBMITTED", "hr", {
                reportId: report.id,
                userId: report.userId,
                userName: report.userName,
                date: report.date,
                totalHours: `${totalH}h ${totalM}min`,
                tasksCount: report.tasks.length,
            });
            hrDataService._dispatchNotification("REMOTE_REPORT_SUBMITTED", "managers", {
                reportId: report.id,
                userId: report.userId,
                userName: report.userName,
                date: report.date,
                totalHours: `${totalH}h ${totalM}min`,
                tasksCount: report.tasks.length,
            });
        }

        return report;
    },

    // ── Window Logic ─────────────────────────────────────────────

    getFormWindow,
    isFormWindowOpen,
    calculateDuration,

    /**
     * Master check: czy zakładka powinna być widoczna.
     */
    async shouldShowTab(userId) {
        const todayISO = new Date().toISOString().split("T")[0];

        // 1. Sprawdź zatwierdzone wnioski o pracę zdalną na dziś
        const approved = await this._getApprovedRemoteForDate(userId, todayISO);
        if (approved.length === 0) {
            // Sprawdź też czy istnieje raport reopened
            const report = this.getReport(userId, todayISO);
            if (report && report.status === "reopened") return true;
            return false;
        }

        // 2. Sprawdź okno czasowe
        const schedule = await this._getSchedule(userId);
        if (isFormWindowOpen(schedule, todayISO)) return true;

        // 3. Sprawdź czy raport już istnieje (do wyświetlenia read-only)
        const report = this.getReport(userId, todayISO);
        if (report && (report.status === "submitted" || report.status === "reopened")) return true;

        return false;
    },

    // ── Reminder ─────────────────────────────────────────────────

    /**
     * Sprawdza czy powinien być wyświetlony mid-day reminder.
     */
    shouldShowMidDayReminder(report, schedule) {
        if (!report || report.status === "submitted") return false;
        if (report.midDayReminderShown) return false;
        if (report.tasks.length > 0) return false;

        const day = getScheduleForDate(schedule, report.date);
        const from = day.from || "08:00";
        const to = day.to || "16:00";

        // Połowa dnia pracy
        const fromMin = parseInt(from.split(":")[0]) * 60 + parseInt(from.split(":")[1]);
        const toMin = parseInt(to.split(":")[0]) * 60 + parseInt(to.split(":")[1]);
        const midMin = fromMin + Math.floor((toMin - fromMin) / 2);

        const nowDate = new Date();
        const nowMin = nowDate.getHours() * 60 + nowDate.getMinutes();

        return nowMin >= midMin;
    },

    /**
     * Oznacza reminder jako wyświetlony.
     */
    markReminderShown(reportId) {
        const report = this.getReportById(reportId);
        if (!report) return;
        report.midDayReminderShown = true;
        this.saveReport(report);
    },

    // ── HR Methods ───────────────────────────────────────────────

    /**
     * Dla HR: zwraca wszystkie oczekujące/overdue raporty.
     */
    getPendingReports() {
        const all = readJSON(STORAGE_KEY, {});
        const pending = [];
        for (const report of Object.values(all)) {
            if (!report || !report.id) continue;
            // Lazy overdue check
            if (report.status === "draft") {
                const windowEnd = new Date(report.windowCloses).getTime();
                if (Date.now() > windowEnd) {
                    report.status = "overdue";
                    report.history.push({
                        action: "OVERDUE",
                        timestamp: now(),
                        details: "Raport nie został złożony w wymaganym terminie.",
                    });
                    this.saveReport(report);
                }
            }
            if (report.status === "draft" || report.status === "overdue") {
                pending.push(report);
            }
        }
        return pending.sort((a, b) => new Date(b.date) - new Date(a.date));
    },

    /**
     * HR otwiera ponownie raport.
     */
    reopenReport(reportId, hrUser) {
        const report = this.getReportById(reportId);
        if (!report) throw new Error("Raport nie znaleziony");

        report.status = "reopened";
        report.reopenedAt = now();
        report.reopenedBy = hrUser
            ? `${hrUser.firstName || ""} ${hrUser.lastName || ""}`.trim() || hrUser.name || "HR"
            : "HR";
        // Rozszerz okno o 24h od teraz
        report.windowCloses = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
        report.history.push({
            action: "REOPENED",
            timestamp: now(),
            details: `Raport ponownie otwarty przez ${report.reopenedBy}. Nowe okno do: ${new Date(report.windowCloses).toLocaleString("pl-PL")}.`,
        });

        this.saveReport(report);

        // Powiadomienie pracownika
        if (hrDataService._dispatchNotification) {
            hrDataService._dispatchNotification("REMOTE_REPORT_REOPENED", report.userId, {
                reportId: report.id,
                date: report.date,
                reopenedBy: report.reopenedBy,
            });
        }

        return report;
    },

    /**
     * Zwraca wszystkie raporty pracownika.
     */
    getReportsForEmployee(userId) {
        const all = readJSON(STORAGE_KEY, {});
        const reports = [];
        for (const [key, report] of Object.entries(all)) {
            if (report && key.startsWith(`${userId}_`)) {
                reports.push(report);
            }
        }
        return reports.sort((a, b) => new Date(b.date) - new Date(a.date));
    },

    /**
     * Zwraca historię zmian raportu.
     */
    getReportHistory(reportId) {
        const report = this.getReportById(reportId);
        return report?.history || [];
    },

    // ── Page-level visibility (Brief 12) ────────────────────────

    /**
     * Sprawdza czy strona "Rozliczenie pracy zdalnej" powinna być widoczna w menu.
     * Zwraca true jeśli istnieją kwalifikujące się dni z zatwierdzonymi wnioskami
     * o pracę zdalną w oknie dostępu (1 dzień przed – 1 dzień po).
     */
    async shouldShowPage(userId) {
        try {
            // [A15-11] BYŁO ZLE: to pytanie „czy pokazać pozycję w menu" pobierało komplet danych
            // do rozliczenia — w tym drugą listę wniosków dziennych, potrzebną WYŁĄCZNIE do statusu
            // każdego dnia, który tu i tak jest wyrzucany (liczy się sama liczba dni). Sidebar odpytuje
            // to co 2 minuty w KAŻDEJ otwartej karcie, więc każdy pracownik generował dwa zapytania
            // do modułu wniosków kadrowych co 120 sekund — także ten, który nigdy nie pracuje zdalnie.
            // SKUTEK DLA UŻYTKOWNIKA: przy kilkuset zalogowanych sesjach to stałe tło zapytań
            // obciążające bazę, czyli wolniejsza cała aplikacja w godzinach szczytu.
            // NAPRAWA: do decyzji o widoczności pomijamy wzbogacanie o statusy. Zbiór DNI liczy się
            // dokładnie tak samo (z zatwierdzonych wniosków o pracę zdalną i okna −1..+1 dnia),
            // więc pozycja w menu pojawia się i znika bez żadnej zmiany — także sama, bez F5.
            const days = await this.getEligibleRemoteDays(userId, { tylkoWidocznosc: true });
            return days.length > 0;
        } catch {
            return false;
        }
    },

    /**
     * Zwraca tablicę kwalifikujących się dni zdalnych z ich statusami raportów.
     * Okno dostępu: 1 dzień PRZED datą zdalnej → 1 dzień PO dacie zdalnej
     * (spójnie z walidacją SERWEROWĄ w HrController — poza tym oknem backend odrzuca 400).
     */
    // [A15-11] `tylkoWidocznosc` = wołający pyta wyłącznie o TO, CZY jakiś dzień się kwalifikuje
    // (pozycja w menu), a nie o status rozliczenia poszczególnych dni. Pomijamy wtedy drugie
    // zapytanie do serwera. Domyślnie false — wszystkie dotychczasowe wywołania działają bez zmian.
    async getEligibleRemoteDays(userId, { tylkoWidocznosc = false } = {}) {
        try {
            const approved = await this._getAllApprovedRemoteRequests(userId);
            // [FIX 2026-08-01] SYNC STATUSU backend→pracownik: dociągnij wnioski dzienne z serwera
            // i nadpisz status lokalnego raportu (submitted/reopened/approved) PRZED budową listy dni.
            // Bez tego „Zwróć do korekty" u przełożonego nigdy nie docierało do formularza pracownika.
            const dailyByDate = tylkoWidocznosc
                ? new Map()
                : this._buildDailyRequestsByDate(await this._getMyDailyReportRequests(userId));
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // [FIX 2026-08-01] DEDUP DNI po dacie (Map) — dwa wnioski REMOTE_OCCASIONAL na tę samą
            // datę dublowały dzień na liście (i zachęcały do podwójnego rozliczenia).
            const daysByDate = new Map();

            for (const req of approved) {
                const dateStr = req.formData?.date;
                if (!dateStr || daysByDate.has(dateStr)) continue;

                const remoteDate = new Date(dateStr + "T00:00:00");
                // Okno dostępu: 1 dzień PRZED do 1 dnia PO pracy zdalnej (jak walidacja serwerowa)
                const windowStart = new Date(remoteDate);
                windowStart.setDate(windowStart.getDate() - 1);
                windowStart.setHours(0, 0, 0, 0);

                const windowEnd = new Date(remoteDate);
                windowEnd.setDate(windowEnd.getDate() + 1);
                windowEnd.setHours(23, 59, 59, 999);

                if (today >= windowStart && today <= windowEnd) {
                    let report = this.getReport(userId, dateStr);
                    const backendReq = dailyByDate.get(dateStr);
                    if (report && backendReq) {
                        report = this._syncReportWithBackendStatus(report, backendReq);
                    }
                    // Brak lokalnego raportu (inna przeglądarka / wyczyszczony localStorage), a wniosek
                    // istnieje na serwerze — pokaż status z backendu zamiast fałszywego „empty".
                    const backendOnlyStatus = (!report && backendReq && isActiveHrStatus(backendReq.status))
                        ? mapHrStatusToReportStatus(backendReq.status)
                        : null;
                    daysByDate.set(dateStr, {
                        date: dateStr,
                        remoteRequestId: req.id,
                        remoteRequestNumber: req.number,
                        // MIEJSCE pracy zdalnej z formData źródłowego wniosku REMOTE_OCCASIONAL
                        address: req.formData?.address || null,
                        reportStatus: report?.status || backendOnlyStatus || "empty",
                        report,
                        // Id wniosku HR raportu (np. do pobrania PDF) — z lokalnego raportu albo z backendu.
                        submittedRequestId: report?.submittedRequestId
                            || (backendReq && isActiveHrStatus(backendReq.status) ? backendReq.id : null),
                        tasksCount: report?.tasks?.length || backendReq?.formData?.tasks?.length || 0,
                        totalMinutes: report?.totalMinutes || 0,
                    });
                }
            }

            // Sortuj po dacie rosnąco
            const eligibleDays = [...daysByDate.values()];
            eligibleDays.sort((a, b) => a.date.localeCompare(b.date));
            return eligibleDays;
        } catch {
            return [];
        }
    },

    /**
     * Pobiera WSZYSTKIE zatwierdzone wnioski o pracę zdalną użytkownika
     * (bez filtra daty — do użycia z getEligibleRemoteDays).
     */
    async _getAllApprovedRemoteRequests(userId) {
        try {
            const response = await apiService.getHrRequests({
                userId,
                typeCode: "REMOTE_OCCASIONAL",
                status: "Completed",
                pageSize: 100,
            });
            const items = (response.data?.items || []).map(r => ({
                id: r.id,
                number: r.number || r.requestNumber,
                formData: typeof r.formDataJson === "string"
                    ? JSON.parse(r.formDataJson)
                    : (r.formData || {}),
            }));
            return items;
        } catch {
            return [];
        }
    },

    // ── Internal helpers ─────────────────────────────────────────

    /**
     * [FIX 2026-08-01] Wnioski dzienne (REMOTE_WORK_DAILY_REPORT) użytkownika z BACKENDU —
     * źródło prawdy do dedupu przed wysyłką i do synchronizacji statusu lokalnego raportu.
     * Nigdy nie rzuca — przy błędzie zwraca [] (wtedy działa dotychczasowa ścieżka lokalna,
     * a duplikat i tak zablokuje dedup serwerowy w HrController).
     */
    async _getMyDailyReportRequests(userId) {
        try {
            const response = await apiService.getHrRequests({
                userId,
                typeCode: "REMOTE_WORK_DAILY_REPORT",
                pageSize: 200,
            });
            return (response.data?.items || []).map((r) => {
                let formData = {};
                try {
                    formData = typeof r.formDataJson === "string"
                        ? JSON.parse(r.formDataJson)
                        : (r.formData || {});
                } catch { /* uszkodzony JSON — wniosek bez daty, pominie go mapa po dacie */ }
                return {
                    id: r.id,
                    requestNumber: r.number || r.requestNumber || null,
                    status: String(r.status || ""),
                    createdAt: r.createdAt || null,
                    formData,
                };
            });
        } catch {
            return [];
        }
    },

    /**
     * Mapa data (YYYY-MM-DD) → wniosek dzienny. Przy wielu wnioskach na tę samą datę wygrywa
     * AKTYWNY (nieodrzucony/nieanulowany); wśród równorzędnych — najnowszy (lista z backendu
     * jest sortowana malejąco po dacie utworzenia).
     */
    _buildDailyRequestsByDate(requests) {
        const byDate = new Map();
        for (const req of requests || []) {
            const date = req.formData?.date;
            if (!date) continue;
            const prev = byDate.get(date);
            if (!prev) { byDate.set(date, req); continue; }
            if (!isActiveHrStatus(prev.status) && isActiveHrStatus(req.status)) byDate.set(date, req);
        }
        return byDate;
    },

    /**
     * [FIX 2026-08-01] Synchronizacja statusu lokalnego raportu (localStorage) ze statusem
     * wniosku HR na backendzie — backend jest źródłem prawdy. Dzięki temu „Zwróć do korekty"
     * u przełożonego (CorrectionRequested) ODBLOKOWUJE formularz pracownika (status reopened
     * + okno 24h jak w reopenReport), a Completed pokazuje raport jako zatwierdzony.
     * Zapis przez saveReport — RemoteWorkReportTab czyta localStorage, więc widzi zmianę
     * bez żadnych modyfikacji po swojej stronie.
     */
    _syncReportWithBackendStatus(report, backendReq) {
        if (!report || !backendReq) return report;
        const mapped = mapHrStatusToReportStatus(backendReq.status);
        if (!mapped) return report;

        if (!Array.isArray(report.history)) report.history = [];
        let changed = false;

        if (isActiveHrStatus(backendReq.status) && report.submittedRequestId !== backendReq.id) {
            report.submittedRequestId = backendReq.id;
            changed = true;
        }

        if (report.status !== mapped) {
            const prevStatus = report.status;
            report.status = mapped;
            if (mapped === "submitted" && !report.submittedAt) report.submittedAt = now();
            if (mapped === "reopened") {
                // Odblokowanie edycji jak w reopenReport: znacznik zwrotu + nowe okno 24h od teraz.
                report.reopenedAt = now();
                report.reopenedBy = report.reopenedBy || "Przełożony / HR";
                report.windowCloses = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
            }
            report.history.push({
                action: "STATUS_SYNCED",
                timestamp: now(),
                details: `Status raportu zsynchronizowany z wnioskiem HR ${backendReq.requestNumber || backendReq.id}: ${prevStatus} → ${mapped} (status wniosku: ${backendReq.status}).`,
            });
            changed = true;
        }

        if (changed) this.saveReport(report);
        return report;
    },

    async _getApprovedRemoteForDate(userId, dateISO) {
        try {
            // Użyj hrDataService jeśli ma metodę
            if (hrDataService.getApprovedRemoteRequestsForDate) {
                return await hrDataService.getApprovedRemoteRequestsForDate(userId, dateISO);
            }
            // Fallback: ręczny odczyt
            const all = JSON.parse(localStorage.getItem("hr_requests") || "[]");
            return all.filter(r =>
                r.userId === userId &&
                r.typeId === "REMOTE_OCCASIONAL" &&
                r.status === HR_REQUEST_STATUS.COMPLETED &&
                r.formData?.date === dateISO
            );
        } catch {
            return [];
        }
    },

    async _getSchedule(userId) {
        try {
            if (hrDataService.getWorkSchedule) {
                return (await hrDataService.getWorkSchedule(userId)) || DEFAULT_SCHEDULE;
            }
            const raw = localStorage.getItem("hr_work_schedules");
            if (raw) {
                const all = JSON.parse(raw);
                return all[userId] || DEFAULT_SCHEDULE;
            }
        } catch (error) { console.error("Błąd kalkulacji raportu:", error); }
        return DEFAULT_SCHEDULE;
    },
};

export { remoteWorkReportService };
export default remoteWorkReportService;
